import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getCookie, clearAllCookies } from '../helpers/cookie';
import { getRefreshToken } from '../helpers/auth';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Zoom
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import clockSound from '../assets/audio/clock.mp3';

const DummyTransition = React.forwardRef(({ children, in: inProp }, ref) => {
  return (
    <div ref={ref} style={{ display: inProp ? 'block' : 'none' }}>
      {children}
    </div>
  );
});

const SessionManager = ({ children }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const rootRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const clockAudioRef = useRef(null);

  if (clockAudioRef.current === null && typeof Audio !== 'undefined') {
    clockAudioRef.current = new Audio(clockSound);
  }

  const dispatch = useDispatch();

  // Parse query parameters for testing overrides on mount, avoiding early returns in component body
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const testTimeoutParam = params.get('TEST_SESSION_TIMEOUT');
      const testWarningParam = params.get('TEST_WARNING_TIME');
      
      // Clear stale test overrides if on login page or when no accessToken is present (and no query parameters are passed)
      const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';
      if ((isLoginPage || !getCookie('accessToken')) && !testTimeoutParam && !testWarningParam) {
        localStorage.removeItem('TEST_SESSION_TIMEOUT');
        localStorage.removeItem('TEST_WARNING_TIME');
      }

      if (params.get('clearTest')) {
        localStorage.removeItem('TEST_SESSION_TIMEOUT');
        localStorage.removeItem('TEST_WARNING_TIME');
        window.location.href = window.location.pathname;
        return;
      }
      
      let changed = false;
      if (testTimeoutParam) {
        localStorage.setItem('TEST_SESSION_TIMEOUT', testTimeoutParam);
        changed = true;
      }
      if (testWarningParam) {
        localStorage.setItem('TEST_WARNING_TIME', testWarningParam);
        changed = true;
      }
      if (changed) {
        window.location.href = window.location.pathname;
      }
    } catch (e) {
      console.error('Error parsing session test query params:', e);
    }
  }, []);

  // Read optional localStorage overrides for testing session timeout easily
  const testTimeout = localStorage.getItem('TEST_SESSION_TIMEOUT');
  const testWarning = localStorage.getItem('TEST_WARNING_TIME');

  const SESSION_TIMEOUT = testTimeout ? parseInt(testTimeout, 10) : 30 * 60 * 1000;
  const WARNING_TIME = Math.min(testWarning ? parseInt(testWarning, 10) : 60 * 1000, SESSION_TIMEOUT);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check token expiration and refresh if needed
  //  const checkTokenExpiration = useCallback(async () => {
  //     const accessToken = getCookie('accessToken');
  //     if (!accessToken) return;

  //     try {
  //       const decoded = jwtDecode(accessToken);
  //       if (!decoded.exp) return;

  //       const expirationTime = decoded.exp * 1000;
  //       const currentTime = Date.now();
  //       const timeRemaining = expirationTime - currentTime;
  //       const fiveMinutesInMs = 5 * 60 * 1000;

  //       console.log('Current time:', new Date(currentTime).toLocaleString());
  //       console.log('Token expires at:', new Date(expirationTime).toLocaleString());
  //       console.log('Time remaining (ms):', timeRemaining);

  //       if (timeRemaining <= fiveMinutesInMs && timeRemaining > 0) {
  //         // console.log('Token expiring soon, refreshing...');
  //         dispatch(getRefreshToken());
  //       }
  //     } catch (error) {
  //       console.error('Error checking token expiration:', error);
  //     }
  //   }, [dispatch]);
  const checkTokenExpiration = useCallback(async () => {
    const refreshToken = getCookie('refreshToken');
    if (!refreshToken) return; // User is not logged in

    const accessToken = getCookie('accessToken');
    let shouldRefresh = false;

    if (!accessToken) {
      // Access token has expired/been cleared, but refresh token is still present.
      shouldRefresh = true;
    } else {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp) {
          const expirationTime = decoded.exp * 1000;
          const currentTime = Date.now();
          const timeRemaining = expirationTime - currentTime;
          const fiveMinutesInMs = 5 * 60 * 1000;

          if (timeRemaining <= fiveMinutesInMs) {
            // Only refresh the token in the background if the user is active
            const storedLastActivity = localStorage.getItem('lastActivity');
            const lastActivity = storedLastActivity ? parseInt(storedLastActivity, 10) : lastActivityRef.current;
            const inactiveDuration = currentTime - lastActivity;

            if (inactiveDuration <= (SESSION_TIMEOUT - WARNING_TIME)) {
              shouldRefresh = true;
            }
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        shouldRefresh = true;
      }
    }

    if (shouldRefresh) {
      const lastRefresh = localStorage.getItem('lastTokenRefresh');
      if (lastRefresh && Date.now() - parseInt(lastRefresh, 10) < 30000) {
        return; // Another tab is currently refreshing the token
      }
      localStorage.setItem('lastTokenRefresh', Date.now().toString());

      try {
        const response = await getRefreshToken();
        if (!response) {
          const publicPaths = ['/login', '/', '/register', '/changepassword', '/support'];
          const isPublic = publicPaths.includes(window.location.pathname) || 
                           window.location.pathname.startsWith('/repository/view') ||
                           window.location.pathname.startsWith('/registration');
          if (!isPublic) {
            window.location.href = '/login';
          }
        }
      } catch (error) {
        console.error('Error refreshing token in SessionManager:', error);
      }
    }
  }, []);

  // Track user activity and check token on activity
  useEffect(() => {
    let lastUpdate = 0;
    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem('lastActivity', now.toString());

      // Throttle checking token expiration on high-frequency events to once every 15s
      if (now - lastUpdate > 15000) {
        lastUpdate = now;
        checkTokenExpiration();
      }
    };

    const events = [
      'click',
      'dblclick',
      'contextmenu',
      'keydown',
      'mousemove',
      'mousedown',
      'scroll',
      'touchstart'
    ];
    events.forEach((event) => window.addEventListener(event, updateActivity, { passive: true }));

    // Handle tab visibility change to sync session and tokens immediately
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const storedLastActivity = localStorage.getItem('lastActivity');
        if (storedLastActivity) {
          lastActivityRef.current = parseInt(storedLastActivity, 10);
        } else {
          lastActivityRef.current = now;
          localStorage.setItem('lastActivity', now.toString());
        }
        checkTokenExpiration();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle hover state
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      events.forEach((event) => window.removeEventListener(event, updateActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [checkTokenExpiration]);

  useEffect(() => {
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [checkTokenExpiration]);

  useEffect(() => {
    // Update last activity on mount to consider opening a tab as activity
    const nowOnMount = Date.now();
    lastActivityRef.current = nowOnMount;
    localStorage.setItem('lastActivity', nowOnMount.toString());

    const isPublicPath = () => {
      const path = window.location.pathname;
      return (
        path === '/login' ||
        path === '/' ||
        path.startsWith('/repository/view') ||
        path.startsWith('/registration') ||
        path === '/register' ||
        path === '/changepassword' ||
        path === '/support'
      );
    };

    const checkSessionTimeout = () => {
      const accessToken = getCookie('accessToken');
      if (!accessToken) {
        setShowWarning(false);
        if (!isPublicPath()) {
          window.location.href = '/login';
        }
        return;
      }

      let expirationTime = 0;
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp) {
          expirationTime = decoded.exp * 1000;
        }
      } catch (error) {
        console.error('Error decoding token in checkSessionTimeout:', error);
      }

      if (!expirationTime) {
        setShowWarning(false);
        return;
      }

      const now = Date.now();
      const tokenRemainingTime = expirationTime - now;

      // Calculate session remaining time based on inactivity (shared lastActivity in localStorage)
      const storedLastActivity = localStorage.getItem('lastActivity');
      const lastActivity = storedLastActivity
        ? parseInt(storedLastActivity, 10)
        : now;
      const timeSinceLastActivity = now - lastActivity;
      const sessionRemainingTime = SESSION_TIMEOUT - timeSinceLastActivity;

      let effectiveRemainingTime = Math.min(tokenRemainingTime, sessionRemainingTime);
      let warningThreshold = WARNING_TIME;

      // Read test overrides for testing timeout easily
      const testTimeout = localStorage.getItem('TEST_SESSION_TIMEOUT');
      const testWarning = localStorage.getItem('TEST_WARNING_TIME');

      if (testTimeout) {
        const customTimeout = parseInt(testTimeout, 10);
        const customRemaining = customTimeout - timeSinceLastActivity;
        effectiveRemainingTime = Math.min(tokenRemainingTime, customRemaining);
        warningThreshold = Math.min(testWarning ? parseInt(testWarning, 10) : 60 * 1000, customTimeout);
      }

      if (effectiveRemainingTime <= warningThreshold && effectiveRemainingTime > 0) {
        setTimeLeft(Math.ceil(effectiveRemainingTime / 1000));
        setShowWarning(true);
      } else if (effectiveRemainingTime <= 0) {
        localStorage.removeItem('lastActivity');
        clearAllCookies();
        window.location.href = '/login';
      } else {
        setShowWarning(false);
      }
    };

    const sessionCheckInterval = setInterval(checkSessionTimeout, 1000);
    return () => clearInterval(sessionCheckInterval);
  }, []);

  useEffect(() => {
    if (showWarning && !!getCookie('accessToken')) {
      if (clockAudioRef.current) {
        clockAudioRef.current.currentTime = 0;
        const playPromise = clockAudioRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((err) => {
            console.warn('Clock audio play blocked by browser autoplay policy:', err);
          });
        }
      }
    }
  }, [showWarning]);

  return (
    <div ref={rootRef}>
      {children}
      <Dialog
        open={showWarning && !!getCookie('accessToken')}
        TransitionComponent={process.env.NODE_ENV === 'test' ? DummyTransition : Zoom}
        TransitionProps={process.env.NODE_ENV === 'test' ? { timeout: 0 } : undefined}
        keepMounted
        aria-describedby="session-expiration-warning"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            padding: '24px',
            maxWidth: '400px',
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
          }
        }}
        sx={{
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        }}
      >
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            {/* Warning Icon with pulse animation */}
            <Box
              sx={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 152, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)' },
                  '70%': { boxShadow: '0 0 0 12px rgba(255, 152, 0, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)' },
                }
              }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: '42px', color: '#ff9800' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} color="#1e293b" sx={{ mt: 1 }}>
              Session Expiration
            </Typography>

            <Typography variant="body1" color="#64748b">
              Your session will expire soon due to inactivity. Please click below to stay signed in.
            </Typography>

            {/* Circular Countdown Progress */}
            <Box position="relative" display="inline-flex" sx={{ my: 2 }}>
              <CircularProgress
                variant="determinate"
                value={(timeLeft / (WARNING_TIME / 1000)) * 100}
                size={90}
                thickness={4}
                sx={{
                  color: timeLeft < 15 ? '#ef4444' : '#3b82f6',
                  transition: 'color 0.5s ease',
                }}
              />
              <Box
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight={800}
                  color="#1e293b"
                  fontFamily="'Roboto Mono', monospace"
                >
                  {formatTime(timeLeft)}
                </Typography>
              </Box>
            </Box>

            {/* Actions */}
            <Box display="flex" width="100%" gap={2} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  localStorage.removeItem('lastActivity');
                  clearAllCookies();
                  window.location.href = '/login';
                }}
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 600,
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  textTransform: 'none',
                  fontSize: '15px',
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    borderColor: '#94a3b8',
                  }
                }}
              >
                Log Out
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  const now = Date.now();
                  lastActivityRef.current = now;
                  localStorage.setItem('lastActivity', now.toString());
                  setShowWarning(false);
                  checkTokenExpiration();
                }}
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 600,
                  backgroundColor: '#3b82f6',
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                  '&:hover': {
                    backgroundColor: '#2563eb',
                    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.35)',
                  }
                }}
              >
                Keep Working
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionManager;